import type { SearchParams } from '@seek/chalice-types';
import type { JobCardLinkProps } from '@seek/job-card';

import { useLinkProps } from 'src/components/NavLink/NavLink.tsx';
import { getRefineSearchQuery } from 'src/components/Search/utils/utils.ts';
import { useJobCardEnrichedLocation } from 'src/modules/enriched-location';
import { useQualifiedLinkParams } from 'src/modules/qualified-location';
import { useSelector } from 'src/store/react';
import {
  selectFeatureFlag,
  selectNormalisedCompanyName,
  selectSearchQuery,
} from 'src/store/selectors';
import type { JobViewModelClassification } from 'src/types/JobViewModel.ts';

export function useClassificationLinks({
  classification: jobClassification,
}: {
  classification: JobViewModelClassification;
}) {
  const normalisedCompanyName = useSelector(selectNormalisedCompanyName);

  const searchParams = getRefineSearchQuery(useSelector(selectSearchQuery));
  const workArrangements = searchParams?.workarrangement || '';
  const isRemoteFilterEnabled = useSelector(
    selectFeatureFlag('remoteSearchFilter'),
  );
  const rel =
    isRemoteFilterEnabled && workArrangements ? 'nofollow' : undefined;

  const getQualifiedLinkParams = useQualifiedLinkParams({
    rel,
    shouldUseUrlResolver: true,
  });
  const getJobCardEnrichedLocation = useJobCardEnrichedLocation();
  const getLinkProps = useLinkProps({
    getQualifiedLinkParams,
  });

  const { classification, subClassification } = jobClassification;
  return [classification, subClassification].reduce<JobCardLinkProps[]>(
    (acc, classificationItem) => {
      if (!classificationItem) {
        return acc;
      }

      const { name, title, params: query } = classificationItem;

      const normalisedQuery: SearchParams = normalisedCompanyName
        ? {
            ...query,
            keywords: normalisedCompanyName,
          }
        : query;

      const linkProps = getLinkProps(
        getJobCardEnrichedLocation({
          pathname: '/jobs',
          query: normalisedQuery,
        }),
      );

      acc.push({
        name,
        title,
        ...linkProps,
      });

      return acc;
    },
    [],
  );
}
